interface NewRelic {
  addPageAction: (name: string, attr: object) => void;
  setCustomAttribute: (name: string, value: string) => void;
  noticeError: (error: Error) => void;
  setCurrentRouteName: (name: string) => void;
  addRelease: (name: string, id: string) => void;
  interaction: () => {
    end: () => void;
    save: () => void;
  };
}

type WindowWithNewRelic = Window & {
  newrelic?: Partial<NewRelic>;
};

/**
 * Reports an error to New Relic
 * @param error - The error to report
 * @param attributes - Optional attributes to include with the error
 */
export const reportError = (error: Error, attributes: Record<string, unknown> = {}): void => {
  if (hasNewRelic(window)) {
    // Set any additional attributes
    Object.entries(attributes).forEach(([key, value]) => {
      if (typeof value === 'string') {
        window.newrelic.setCustomAttribute(key, value);
      }
    });
    
    // Report the error
    window.newrelic.noticeError(error);
  }
};

const hasNewRelic = (win: Window): win is Window & { newrelic: NewRelic } => {
  const typedWindow = win as WindowWithNewRelic;
  return typeof typedWindow.newrelic?.addPageAction === 'function';
};

/**
 * Tracks a page action in New Relic
 * @param name - The name of the action to track
 * @param attributes - Optional attributes to include
 */
/**
 * Sets custom attributes in New Relic
 * @param attributes - Array of attributes to set, each with a name and value
 */
export const setCustomAttributes = (attributes: Array<{ name: string; value: string | number }>): void => {
  if (hasNewRelic(window)) {
    attributes.forEach(({ name, value }) => {
      if (typeof value === 'string' || typeof value === 'number') {
        window.newrelic.setCustomAttribute(name, String(value));
      }
    });
  }
};

/**
 * Tracks a page action in New Relic
 * @param name - The name of the action to track
 * @param attributes - Optional attributes to include
 */
export const trackPageAction = (name: string, attributes: Record<string, unknown> = {}): void => {
  if (hasNewRelic(window)) {
    window.newrelic.addPageAction(name, attributes);
  }
};

/**
 * Tracks a CTA click in New Relic
 * @param name - The name of the CTA action
 * @param url - The URL that was clicked
 */
export const trackCTAClick = (name: string, url: string): void => {
  trackPageAction(name, {
    rvClickIDURL: url
  });
};
